import { Routes, Route, useNavigate } from "react-router";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Index from "./pages/Index";
import Saved from "./pages/Saved";
import Tutorial from "./pages/Tutorial";
import Subscribe from "./pages/Subscribe";
import Verify from "./pages/Verify";
import Onboarding from "./pages/Onboarding";
import Community from "./pages/Community";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Spinner } from "@/components/ui/spinner";
import { MainLayout } from "@/components/layouts/MainLayout";
import { isUserSubscribed } from "@/lib/supabase";
import ResetPassword from "./pages/ResetPassword";
import { useLoadCategories } from "./hooks/useLoadCategories";
import Profile from "./pages/Profile";
import { LeaderboardPage } from "./pages/LeaderboardPage";
import Post from "./pages/Post";
import AdminPanel from "./pages/Index/AdminPanel";
import { useUser } from "@supabase/auth-helpers-react";
import { ADMIN_IDS } from "./consts/admin";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
}

const ProtectedRoute = ({
  children,
  requireSubscription = false,
}: ProtectedRouteProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);
  const navigate = useNavigate();
  useLoadCategories();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Normal auth check flow
        const {
          data: { session },
        } = await supabase.auth.getSession(); // TODO: move to edge function and propagate JWT for further access

        if (!session) {
          navigate("/login", { replace: true });
          return;
        }

        if (
          requireSubscription &&
          !window.location.href.includes("resetpassword")
        ) {
          const isSubscribed = await isUserSubscribed(session.user.id);
          if (!isSubscribed) {
            navigate("/verify", { replace: true });
            return;
          }
          setIsAllowed(true);
        } else {
          setIsAllowed(true);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        navigate("/login", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [requireSubscription, navigate]);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Only render children if explicitly allowed
  return isAllowed ? <>{children}</> : null;
};


export const AdminProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const user = useUser();
  const [checking, setChecking] = useState(true);

  useLoadCategories();

  useEffect(() => {
    if (!user) return;

    if (!ADMIN_IDS.includes(user.id)) {
      navigate("/", { replace: true });
    } else {
      setChecking(false);
    }
  }, [user, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) {
        navigate("/resetpassword", { replace: true });
      }
    });
  }, []);
  
  return (
    <Routes>
      <Route path="login" element={<Login />} />
      <Route path="register" element={<Register />} />
      <Route path="resetpassword" element={<ResetPassword />} />
      <Route path="/subscribe" element={<Subscribe />} />
      <Route
        path="/"
        element={
          <ProtectedRoute requireSubscription>
            <Index />
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute requireSubscription>
            <Community />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved"
        element={
          <ProtectedRoute requireSubscription>
            <Saved />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tutorial/:documentId"
        element={
          <ProtectedRoute requireSubscription>
            <Tutorial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verify"
        element={
          <ProtectedRoute>
            <Verify />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/:userId"
        element={
          <ProtectedRoute requireSubscription>
            <MainLayout isProfile={true}>
              <Profile />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireSubscription>
            <MainLayout>
              <Onboarding />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute requireSubscription>
            <MainLayout>
              <LeaderboardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/post/:id"
        element={
          <ProtectedRoute requireSubscription>
            <MainLayout>
              <Post />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireSubscription>
            <AdminProtectedRoute>
              <MainLayout>
                <AdminPanel />
              </MainLayout>
            </AdminProtectedRoute>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
